import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageContext } from '../../shared/contexts/LanguageContext';

const languages = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'it', label: 'Italiano' },
  { code: 'pt', label: 'Português' },
  { code: 'zh', label: '中文' },
  { code: 'ja', label: '日本語' },
  { code: 'ko', label: '한국어' },
  { code: 'ru', label: 'Русский' },
  { code: 'tr', label: 'Türkçe' },
  { code: 'ar', label: 'العربية' },
  { code: 'hi', label: 'हिन्दी' },
];

const LanguageSelector = ({
  onClose,
  offsetY = 16, // default offset
}: {
  onClose: () => void;
  offsetY?: number;
}) => {
  
const { i18n, t } = useTranslation();
const langCtx = useContext(LanguageContext);
const current = langCtx?.language || i18n.language;

const handleSelect = (langCode: string) => {
  langCtx?.changeLanguage(langCode);
  onClose();
};

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('languageSelector.title')}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        backgroundColor: 'rgba(0,0,0,0.65)',
        backdropFilter: 'blur(8px)',
      }}
    >
      {/* Single centered card */}
      <div
        onMouseDown={(e) => e.stopPropagation()} // prevent close when clicking inside
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: `translate(-50%, -50%) translateY(${offsetY}px)`,
          width: 'min(600px, calc(100vw - 32px))',            // ✅ wider than login window
          maxHeight: 'calc(100dvh - 16px)',
          overflow: 'hidden',

          borderRadius: 24,
          border: '1px solid rgba(148,163,184,0.3)',
          background:
            'linear-gradient(145deg, rgba(15,23,42,0.98), rgba(2,6,23,0.98))',
          boxShadow: '0 25px 60px rgba(0,0,0,0.9)',
          padding: 16,
          color: '#e5e7eb',
        }}
      >
        {/* header row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 12,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 18,
                fontWeight: 700,
                letterSpacing: -0.02,
                color: '#b1b8c7',
                marginBottom: 6,
              }}
            >
              {t('languageSelector.title')}
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8' }}>
              {t('languageSelector.subtitle', 'Choose your language')}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label={t('languageSelector.close')}
            style={{
              width: 36,
              height: 36,
              borderRadius: 12,
              border: '1px solid rgba(148,163,184,0.25)',
              background: 'rgba(255,255,255,0.06)',
              color: '#b1b8c7',
              cursor: 'pointer',
              flex: '0 0 auto',
            }}
          >
            ✕
          </button>
        </div>

        {/* divider */}
        <div
          style={{
            height: 1,
            width: '100%',
            background: 'linear-gradient(to right, transparent, #1f2937, transparent)',
            marginBottom: 12,
          }}
        />

        {/* scrollable list */}
        <div
          className="scroll-y"
          style={{
            maxHeight: 'min(65vh, 620px)',

            overflowY: 'auto',
            paddingRight: 4,
          }}
        >
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'grid',
              gap: 8,
            }}
          >
            {languages.map((lang) => {
              const active = current?.startsWith(lang.code);
              return (
                <li key={lang.code}>
                  <button
                    type="button"
                    onClick={() => handleSelect(lang.code)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 10,
                      padding: '10px 12px',
                      borderRadius: 14,
                      cursor: 'pointer',
                      border: active
                        ? '1px solid rgba(177,184,199,0.55)'
                        : '1px solid rgba(148,163,184,0.22)',
                      background: active
                        ? 'rgba(177,184,199,0.14)'
                        : 'rgba(255,255,255,0.04)',
                      color: '#e5e7eb',
                      fontSize: 14,
                      fontWeight: active ? 700 : 600,
                      textAlign: 'left',
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 999,
                          background: active ? '#a3e635' : 'rgba(148,163,184,0.4)',
                        }}
                      />
                      {lang.label}
                    </span>

                    <span style={{ color: '#94a3b8', fontSize: 12 }}>
                      {active ? t('languageSelector.active', 'Active') : ''}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {/* footer */}
        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '10px 14px',
              borderRadius: 999,
              border: '1px solid #4b5563',
              background: 'transparent',
              color: '#e5e7eb',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            {t('languageSelector.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LanguageSelector;
